import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import useAuthStore from '../context/authStore';
import api from '../services/api';
import ProtectedVideoPlayer from './ProtectedVideoPlayer';
import toast from 'react-hot-toast';
import './LessonPage.css';

export default function LessonPage() {
  const { courseId, lessonIndex: liParam } = useParams();
  const lessonIndex = parseInt(liParam) || 0;
  const navigate    = useNavigate();
  const { user, userDoc } = useAuthStore();

  /* ── state ── */
  const [course,      setCourse]      = useState(null);
  const [enState,     setEnState]     = useState(null);
  const [access,      setAccess]      = useState(null);
  const [resources,   setResources]   = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [sidebarOpen, setSidebar]     = useState(true);
  const [activeTab,   setActiveTab]   = useState('overview'); // overview | resources | quiz
  const [payModal,    setPayModal]    = useState(false);
  const [payMethod,   setPayMethod]   = useState('card');
  const [phone,       setPhone]       = useState('');
  const [paying,      setPaying]      = useState(false);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [quizResult,  setQuizResult]  = useState(null);
  const [submittingQuiz, setSubmitting] = useState(false);

  const saveTimer = useRef(null);

  const lesson = course?.lessons?.[lessonIndex];

  /* ── load ── */
  const loadAll = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [cRes, stateRes, accessRes] = await Promise.all([
        api.get(`/courses/${courseId}`),
        api.get(`/lessons/${courseId}/enrollment-state`).catch(() => ({ data: {} })),
        api.get(`/lessons/${courseId}/${lessonIndex}/access`).catch(() => ({ data: { allowed: false, reason: 'unknown' } })),
      ]);
      setCourse(cRes.data);
      setEnState(stateRes.data);
      setAccess(accessRes.data);
      if (accessRes.data?.allowed) {
        const rRes = await api.get(`/lessons/${courseId}/${lessonIndex}/resources`).catch(() => ({ data: { pdfs: [], quiz: null } }));
        setResources(rRes.data);
      }
    } catch {
      toast.error('تعذّر تحميل الدرس');
    } finally {
      setLoading(false);
    }
  }, [courseId, lessonIndex, user]);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* ── auto-save progress every 20s ── */
  useEffect(() => {
    if (!access?.allowed) return;
    const save = () => api.post(`/lessons/${courseId}/${lessonIndex}/progress`, { timestamp: 0, completed: false }).catch(() => {});
    saveTimer.current = setInterval(save, 20000);
    return () => clearInterval(saveTimer.current);
  }, [access, courseId, lessonIndex]);

  /* ── helpers ── */
  const isCompleted  = (idx) => (enState?.completedLessons || []).includes(`lesson_${idx}`);
  const isPaid       = (idx) => {
    if (!course) return false;
    if (userDoc?.role === 'teacher' && course.teacherId === user?.uid) return true;
    if (userDoc?.role === 'admin') return true;
    if (course.paymentType !== 'perLesson') return !!enState?.enrolled;
    if (idx === 0 || course.lessons?.[idx]?.isFree) return true;
    return (enState?.paidLessons || []).includes(idx);
  };

  const goTo = (idx) => navigate(`/courses/${course?.slug || courseId}/lessons/${idx}`);

  const markComplete = async () => {
    try {
      const r = await api.post(`/lessons/${courseId}/${lessonIndex}/progress`, { timestamp: 0, completed: true });
      setEnState(p => ({ ...p, ...r.data }));
      toast.success('✅ تم تسجيل إتمام الدرس!');
      if (r.data.progress >= 100) toast.success('🎓 أكملت الكورس بالكامل! الشهادة جاهزة في لوحة التحكم.');
    } catch { toast.error('حدث خطأ في حفظ التقدم'); }
  };

  /* ── per-lesson pay ── */
  const handlePay = async () => {
    if (payMethod === 'wallet' && !phone) return toast.error('أدخل رقم المحفظة');
    setPaying(true);
    try {
      const r = await api.post('/payments/checkout', {
        courseId, method: payMethod,
        phoneNumber: phone || undefined,
        lessonIndex,
      });
      if (r.data.enrolled) { toast.success('🎉 تم فتح الدرس!'); setPayModal(false); loadAll(); }
      else if (r.data.method === 'wallet' && r.data.redirectUrl) window.location.href = r.data.redirectUrl;
      else if (r.data.method === 'card'   && r.data.iframeUrl)   window.open(r.data.iframeUrl, '_blank');
    } catch (err) { toast.error(err.response?.data?.error || 'حدث خطأ'); }
    setPaying(false);
  };

  /* ── quiz submit ── */
  const handleQuizSubmit = async () => {
    const quiz = resources?.quiz;
    if (!quiz) return;
    const unanswered = (quiz.questions || []).filter(q => quizAnswers[q.id] === undefined);
    if (unanswered.length) return toast.error(`أجب على ${unanswered.length} سؤال متبقي`);
    setSubmitting(true);
    try {
      const r = await api.post(`/quizzes/${quiz.id}/submit`, { answers: quizAnswers });
      setQuizResult(r.data);
    } catch (err) { toast.error(err.response?.data?.error || 'حدث خطأ'); }
    setSubmitting(false);
  };

  /* ── guards ── */
  if (!user) return (
    <div className="lp-gate">
      <div>🔒</div>
      <h2>سجّل دخولك لمشاهدة الدروس</h2>
      <Link to="/login" className="btn btn-primary">تسجيل الدخول</Link>
    </div>
  );
  if (loading) return <div className="lp-loading"><div className="spinner" /></div>;
  if (!course)  return <div className="lp-gate"><div>❌</div><h2>الكورس غير موجود</h2><Link to="/courses" className="btn btn-outline">تصفح الكورسات</Link></div>;

  const total    = course.lessons?.length || 0;
  const progress = enState?.progress || 0;
  const lessonPrice = lesson?.price || course?.price || 0;
  const isPerLesson = course.paymentType === 'perLesson';

  return (
    <div className="lp-root">

      {/* ══ TOP BAR ══════════════════════════════════════════════════════════ */}
      <header className="lp-topbar">
        <Link to={`/courses/${course?.slug || courseId}`} className="lp-back" title="رجوع للكورس">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          <span className="lp-back-title">{course.title}</span>
        </Link>

        <div className="lp-progress-wrap">
          <div className="lp-progress-bar">
            <div className="lp-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <span className="lp-progress-txt">{progress}% مكتمل</span>
        </div>

        <button className="lp-toggle-btn" onClick={() => setSidebar(!sidebarOpen)}>
          {sidebarOpen
            ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="9" y1="3" x2="9" y2="21"/></svg>
            : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="15" y1="3" x2="15" y2="21"/></svg>
          }
          <span>محتوى الكورس</span>
        </button>
      </header>

      {/* ══ BODY ═════════════════════════════════════════════════════════════ */}
      <div className="lp-body">

        {/* ── MAIN ── */}
        <main className="lp-main">

          {/* Video area */}
          <div className="lp-video-area">
            {access?.allowed ? (
              <ProtectedVideoPlayer
                publicId={lesson?.cloudinaryPublicId || undefined}
                directUrl={lesson?.directVideoUrl    || undefined}
                title={lesson?.title}
              />
            ) : (
              <div className="lp-locked">
                <div className="lp-locked-icon">🔒</div>
                <h3 className="lp-locked-title">
                  {access?.reason === 'not_enrolled'    && 'اشترك في الكورس لمشاهدة الدروس'}
                  {access?.reason === 'lesson_not_paid' && 'ادفع لفتح هذا الدرس'}
                  {!['not_enrolled','lesson_not_paid'].includes(access?.reason) && 'غير مصرح بمشاهدة هذا الدرس'}
                </h3>
                {access?.reason === 'not_enrolled' && (
                  <Link to={`/courses/${course?.slug || courseId}`} className="btn btn-primary btn-lg">اشترك الآن</Link>
                )}
                {access?.reason === 'lesson_not_paid' && (
                  <>
                    <div className="lp-locked-price">{lessonPrice} ج.م</div>
                    <button className="btn btn-primary btn-lg" onClick={() => setPayModal(true)}>
                      💳 ادفع وشاهد الدرس
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Lesson meta + controls */}
          <div className="lp-meta-bar">
            <div className="lp-meta-left">
              <h1 className="lp-lesson-title">
                {lessonIndex + 1}. {lesson?.title || `درس ${lessonIndex + 1}`}
              </h1>
              {lesson?.duration && <span className="lp-lesson-dur">⏱ {lesson.duration}</span>}
            </div>
            <div className="lp-meta-right">
              {access?.allowed && !isCompleted(lessonIndex) && (
                <button className="lp-complete-btn" onClick={markComplete}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="20 6 9 17 4 12"/></svg>
                  تم الإتمام
                </button>
              )}
              {isCompleted(lessonIndex) && (
                <span className="lp-done-badge">✓ مكتمل</span>
              )}
            </div>
          </div>

          {/* Prev / Next navigation */}
          <div className="lp-nav-row">
            <button className="btn btn-outline" onClick={() => goTo(lessonIndex - 1)} disabled={lessonIndex === 0}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
              الدرس السابق
            </button>
            <button className="btn btn-primary" onClick={() => goTo(lessonIndex + 1)} disabled={lessonIndex >= total - 1}>
              الدرس التالي
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
            </button>
          </div>

          {/* ── TABS (Overview / Resources / Quiz) ── */}
          {access?.allowed && (
            <div className="lp-tabs-wrap">
              <div className="lp-tabs">
                {[
                  { key: 'overview',   label: '📋 نظرة عامة' },
                  { key: 'resources',  label: `📎 ملفات${resources?.pdfs?.length > 0 ? ` (${resources.pdfs.length})` : ''}` },
                  { key: 'quiz',       label: `📝 اختبار${resources?.quiz ? '' : ' —'}`, disabled: !resources?.quiz },
                ].map(t => {
                  if (t.key === 'quiz') {
                    const strategies = course?.quizStrategies || ['per_lesson'];
                    const isLastLesson = lessonIndex === (course?.lessons?.length || 0) - 1;
                    const lessonObj = course?.lessons?.[lessonIndex];

                    const showPerLesson = strategies.includes('per_lesson');
                    const showEnd = strategies.includes('end_of_course') && isLastLesson;
                    const showUnit = strategies.includes('per_unit') && lessonObj?.isUnitEnd;

                    if (!showPerLesson && !showEnd && !showUnit) return null;
                  }

                  return (
                    <button
                      key={t.key}
                      className={`lp-tab ${activeTab === t.key ? 'active' : ''} ${t.disabled ? 'disabled' : ''}`}
                      onClick={() => !t.disabled && setActiveTab(t.key)}
                    >{t.label}</button>
                  );
                })}
              </div>

              {/* Overview */}
              {activeTab === 'overview' && (
                <div className="lp-panel">
                  {lesson?.description && <p className="lp-desc">{lesson.description}</p>}
                  {lesson?.keyPoints?.length > 0 && (
                    <div className="lp-kp-wrap">
                      <h4>💡 النقاط الرئيسية</h4>
                      <ul className="lp-kp-list">
                        {lesson.keyPoints.map((k, i) => <li key={i}>{k}</li>)}
                      </ul>
                    </div>
                  )}
                  {lesson?.summary && (
                    <div className="lp-summary-wrap">
                      <h4>📋 ملخص الدرس</h4>
                      <p>{lesson.summary}</p>
                    </div>
                  )}
                  {!lesson?.description && !lesson?.keyPoints?.length && !lesson?.summary && (
                    <p className="lp-empty-panel">لا توجد ملاحظات إضافية لهذا الدرس.</p>
                  )}
                </div>
              )}

              {/* Resources */}
              {activeTab === 'resources' && (
                <div className="lp-panel">
                  {!resources?.pdfs?.length ? (
                    <p className="lp-empty-panel">لا توجد ملفات لهذا الدرس.</p>
                  ) : (
                    <div className="lp-pdfs">
                      {resources.pdfs.map(p => {
                        const name = p.fileName || 'ملف';
                        const dl   = name.toLowerCase().endsWith('.pdf') ? name : name + '.pdf';
                        return (
                          <a key={p.id} href={p.fileURL} target="_blank" rel="noreferrer" download={dl} className="lp-pdf-row">
                            <span className="lp-pdf-icon">📄</span>
                            <span className="lp-pdf-name">{name}</span>
                            {p.size && <span className="lp-pdf-size">{p.size > 1048576 ? (p.size/1048576).toFixed(1)+' MB' : Math.round(p.size/1024)+' KB'}</span>}
                            <span className="lp-pdf-dl">⬇ تحميل</span>
                          </a>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Quiz */}
              {activeTab === 'quiz' && resources?.quiz && (
                <div className="lp-panel">
                  {quizResult ? (
                    <div className="lp-quiz-result">
                      <div className={`lqr-score ${quizResult.passed ? 'pass' : 'fail'}`}>{quizResult.score}%</div>
                      <div className="lqr-msg">{quizResult.message}</div>
                      <div className="lqr-stats">{quizResult.correct} / {quizResult.total} إجابة صحيحة</div>
                      <div className="lqr-answers">
                        {quizResult.results?.map((r, i) => (
                          <div key={i} className={`lqr-row ${r.isCorrect ? 'correct' : 'wrong'}`}>
                            <div className="lqr-q">{i+1}. {r.questionText}</div>
                            <div className="lqr-a">
                              إجابتك: <b>{r.studentAnswer}</b>
                              {!r.isCorrect && <span> ✗ (الصحيح: {r.correctAnswer})</span>}
                              {r.isCorrect  && <span> ✓</span>}
                            </div>
                            {!r.isCorrect && r.explanation && <div className="lqr-exp">💡 {r.explanation}</div>}
                          </div>
                        ))}
                      </div>
                      <button className="btn btn-outline btn-sm" onClick={() => { setQuizResult(null); setQuizAnswers({}); }}>
                        🔄 إعادة المحاولة
                      </button>
                    </div>
                  ) : (
                    <div className="lp-quiz">
                      <div className="lp-quiz-header">
                        <h3>{resources.quiz.title}</h3>
                        <span>{resources.quiz.questions?.length} سؤال</span>
                      </div>
                      <div className="lp-questions">
                        {(resources.quiz.questions || []).map((q, i) => (
                          <div key={q.id} className="lp-question">
                            <div className="lp-q-text">{i+1}. {q.questionText}</div>
                            <div className="lp-options">
                              {(q.type === 'true_false' ? ['صح','خطأ'] : q.options || []).map((opt, oi) => (
                                <label key={oi} className={`lp-option ${quizAnswers[q.id] === opt ? 'selected' : ''}`}>
                                  <input type="radio" name={q.id} onChange={() => setQuizAnswers(p => ({ ...p, [q.id]: opt }))} />
                                  {opt}
                                </label>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                      <button className="btn btn-primary" onClick={handleQuizSubmit} disabled={submittingQuiz}>
                        {submittingQuiz ? <div className="spinner" style={{width:16,height:16,borderWidth:2}}/> : 'تسليم الاختبار ✓'}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Next lesson card */}
          {access?.allowed && lessonIndex < total - 1 && (() => {
            const next = course.lessons[lessonIndex + 1];
            const canWatch = isPaid(lessonIndex + 1);
            return (
              <div className="lp-next-card">
                <div className="lp-next-label">الدرس التالي →</div>
                <div className="lp-next-info">
                  <div className="lp-next-num">{lessonIndex + 2}</div>
                  <div>
                    <div className="lp-next-title">{next.title}</div>
                    {next.duration && <div className="lp-next-dur">{next.duration}</div>}
                  </div>
                </div>
                <button
                  className={`btn ${canWatch ? 'btn-primary' : 'btn-outline'} btn-sm`}
                  onClick={() => canWatch ? goTo(lessonIndex+1) : setPayModal(true)}
                >
                  {canWatch ? 'ابدأ الآن ▶' : `🔒 ${next.price || course.price || 0} ج.م`}
                </button>
              </div>
            );
          })()}
        </main>

        {/* ── SIDEBAR ── */}
        {sidebarOpen && (
          <aside className="lp-sidebar">
            <div className="lp-sb-header">
              <strong>محتوى الكورس</strong>
              <span>{total} درس · {progress}% مكتمل</span>
            </div>

            {/* Overall progress bar in sidebar */}
            <div className="lp-sb-progress">
              <div className="lp-sb-progress-fill" style={{ width: `${progress}%` }} />
            </div>

            <div className="lp-sb-list">
              {(course.lessons || []).map((l, i) => {
                const active    = i === lessonIndex;
                const done      = isCompleted(i);
                const paid      = isPaid(i);
                const price     = l.price || (isPerLesson && i > 0 ? course.price : 0);
                return (
                  <button
                    key={l.id || i}
                    className={`lp-sb-item ${active ? 'active' : ''} ${done ? 'done' : ''} ${!paid && i !== 0 ? 'locked' : ''}`}
                    onClick={() => paid || l.isFree ? goTo(i) : setPayModal(true)}
                    title={l.title}
                  >
                    {/* Status icon */}
                    <div className="lp-sb-icon">
                      {done    ? <span className="lp-icon-done">✓</span>
                       : active ? <span className="lp-icon-play">▶</span>
                       : !paid && i !== 0 ? <span className="lp-icon-lock">🔒</span>
                       : <span className="lp-icon-num">{i + 1}</span>}
                    </div>

                    <div className="lp-sb-info">
                      <div className="lp-sb-title">{l.title || `درس ${i+1}`}</div>
                      <div className="lp-sb-meta">
                        {l.duration && <span>{l.duration}</span>}
                        {l.isFree   && <span className="lp-free-tag">مجاني</span>}
                        {!paid && i !== 0 && isPerLesson && (
                          <span className="lp-price-tag">{price} ج.م</span>
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </aside>
        )}
      </div>

      {/* ══ PAY MODAL ════════════════════════════════════════════════════════ */}
      {payModal && (
        <div className="lp-modal-overlay" onClick={() => setPayModal(false)}>
          <div className="lp-modal" onClick={e => e.stopPropagation()}>
            <div className="lp-modal-top">
              <h3>فتح الدرس</h3>
              <button onClick={() => setPayModal(false)}>✕</button>
            </div>
            <p className="lp-modal-sub">
              <strong>{lesson?.title}</strong>
            </p>
            <div className="lp-modal-price">{lessonPrice} ج.م</div>

            <div className="lp-method-row">
              {[{ v:'card', label:'💳 بطاقة بنكية' }, { v:'wallet', label:'📱 محفظة إلكترونية' }].map(m => (
                <button key={m.v} className={`lp-method-btn ${payMethod === m.v ? 'active' : ''}`}
                  onClick={() => setPayMethod(m.v)}>{m.label}</button>
              ))}
            </div>

            {payMethod === 'wallet' && (
              <div className="form-group" style={{ marginBottom: 14 }}>
                <label className="form-label">رقم المحفظة</label>
                <input className="form-input" placeholder="01XXXXXXXXX" value={phone}
                  onChange={e => setPhone(e.target.value)} dir="ltr" />
              </div>
            )}

            <button className="btn btn-primary" style={{ width:'100%', justifyContent:'center' }}
              onClick={handlePay} disabled={paying}>
              {paying
                ? <div className="spinner" style={{width:18,height:18,borderWidth:2}}/>
                : `ادفع ${lessonPrice} ج.م`}
            </button>

            <p style={{ fontSize:11, color:'var(--text-muted)', textAlign:'center', marginTop:10 }}>
              🔒 مدفوعات آمنة عبر Paymob — مرخّصة من البنك المركزي المصري
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
